import Fuse from 'fuse.js';

interface SiteTreeSearchItem {
    id: string;
    title: string;
    children?: SiteTreeSearchItem[];
}

interface SiteTreeSearchEntry {
    id: string;
    title: string;
}

const SITE_TREE_FUSE_CACHE = new WeakMap<SiteTreeSearchItem[], Fuse<SiteTreeSearchEntry>>();

/** Filter site tree items by title while keeping their ancestor structure. */
export function searchSiteTreeItems<Item extends SiteTreeSearchItem>(
    items: Item[],
    query: string
): Item[] {
    const normalizedQuery = normalizeExactMatchValue(query);
    const fuseQuery = normalizedQuery.length > 0 ? normalizedQuery : query;
    const results = getCachedSiteTreeFuse(items)
        .search(fuseQuery)
        .sort((left, right) => {
            const leftIsExact = normalizeExactMatchValue(left.item.title) === normalizedQuery;
            const rightIsExact = normalizeExactMatchValue(right.item.title) === normalizedQuery;
            return Number(rightIsExact) - Number(leftIsExact);
        });
    const matchedItemRanks = new Map(
        results.map((result, rank) => [result.item.id, rank] as const)
    );

    return filterSiteItemsByRank(items, matchedItemRanks).map(({ item }) => item);
}

function getCachedSiteTreeFuse(items: SiteTreeSearchItem[]): Fuse<SiteTreeSearchEntry> {
    const cachedFuse = SITE_TREE_FUSE_CACHE.get(items);
    if (cachedFuse) {
        return cachedFuse;
    }

    const fuse = new Fuse(flattenSiteItemsForSearch(items), {
        keys: ['title'],
        threshold: 0.35,
        ignoreLocation: true,
    });

    SITE_TREE_FUSE_CACHE.set(items, fuse);
    return fuse;
}

function normalizeExactMatchValue(value: string): string {
    const normalized = value.trim().toLocaleLowerCase();
    const quote = normalized[0];
    if ((quote === '"' || quote === "'") && normalized.at(-1) === quote) {
        return normalized.slice(1, -1).trim();
    }
    return normalized;
}

function flattenSiteItemsForSearch(items: SiteTreeSearchItem[]): SiteTreeSearchEntry[] {
    return items.flatMap((item) => {
        const currentItem = { id: item.id, title: item.title };

        if (item.children) {
            return [currentItem, ...flattenSiteItemsForSearch(item.children)];
        }

        return [currentItem];
    });
}

function filterSiteItemsByRank<Item extends SiteTreeSearchItem>(
    items: Item[],
    matchedItemRanks: Map<string, number>
): Array<{ item: Item; rank: number }> {
    return items
        .flatMap((item) => {
            const rank = matchedItemRanks.get(item.id);
            const children = item.children
                ? filterSiteItemsByRank(item.children, matchedItemRanks)
                : [];
            const bestChildRank = children[0]?.rank;

            if (rank !== undefined) {
                return [
                    {
                        item,
                        rank: bestChildRank !== undefined ? Math.min(rank, bestChildRank) : rank,
                    },
                ];
            }

            if (bestChildRank === undefined) {
                return [];
            }

            return [
                {
                    item: { ...item, children: children.map((child) => child.item) } as Item,
                    rank: bestChildRank,
                },
            ];
        })
        .sort((left, right) => left.rank - right.rank);
}
